import { useQuery } from '@tanstack/react-query'
import _ from 'lodash'
import { ArrowRight, FileText } from 'lucide-react'
import PropTypes from 'prop-types'
import { useMemo } from 'react'
import { Link } from 'react-router'

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/elements/accordion'
import { Button } from '@/components/elements/button'
import { Skeleton } from '@/components/elements/skeleton'
import { QuizResult } from '@/components/QuizResult'
import StatusIcon from '@/components/StatusIcon/StatusIcon'
import QuizAttemptsPanel from '@/pages/Accounts/Instructors/Students/QuizAttemptsPanel'
import { getQuizResult, getStudentCourseContentRecords } from '@/services/student_courses.service'
import { getNextIncompleteCourseRecord } from '@/utils/certificates'
import { scoreFormatter } from '@/utils/formatters'

import CertHero from './CertHero'
import { TYPE_ICON } from './CourseStepList'

function CourseComplete({
  courseRecord,
  openToContent,
  entityId,
  includeReviewLink = true,
  isInstructorView = false,
}) {
  // expand=quiz_attempts serves double duty: managers get the full attempt
  // list batched in (so the per-quiz QuizAttemptsPanel below needn't fire its
  // own request), while students get a bare count — which also keeps this
  // response shape-identical to CourseModal/CourseOverview's fetches on the
  // shared student queryKey.
  const queryKey = isInstructorView
    ? ['accounts', 'instructors', 'students', 'courses', courseRecord.id, 'with-attempts']
    : ['accounts', 'students', 'courses', courseRecord.id]
  const { data: courseContentRecords, isPending } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await getStudentCourseContentRecords(entityId, courseRecord.id, {
        includeQuizAttempts: true,
      })
      return response.results
    },
  })

  const orderedContentRecords = useMemo(() => {
    if (!courseContentRecords) return []
    const groups = _.groupBy(courseContentRecords, (c) => c.content.type === 'Quiz Only')
    return [...(groups.true ?? []), ...(groups.false ?? [])]
  }, [courseContentRecords])

  const stats = useMemo(() => {
    if (!courseContentRecords) return { steps: 0, quizzesPassed: 0, hasQuizzes: false }
    const steps = courseContentRecords.filter((r) => r.status === 'Completed').length
    const quizRecords = courseContentRecords.filter((r) => !!r.content?.quiz)
    const quizzesPassed = quizRecords.filter((r) => r.status === 'Completed').length
    return { steps, quizzesPassed, hasQuizzes: quizRecords.length > 0 }
  }, [courseContentRecords])

  const nextCourse = useMemo(() => getNextIncompleteCourseRecord(courseRecord), [courseRecord])

  if (isPending) {
    return (
      <div className="mx-auto mt-4 flex w-full max-w-3xl flex-col gap-6">
        {/* Hero and result-list placeholders, in the loaded page's column width */}
        <Skeleton className="h-40 w-full" />
        <div className="flex flex-col gap-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </div>
    )
  }

  return (
    // Same content column as CourseOverview so the two states read as one
    // page; the hero pins its own width, this aligns everything below it.
    <div className="mx-auto mt-4 flex w-full max-w-3xl flex-col gap-6">
      <CertHero
        courseRecord={courseRecord}
        entityId={entityId}
        variant="complete"
        isInstructorView={isInstructorView}
      />

      {nextCourse && (
        <section className="bg-primary/10 border-primary/30 flex flex-col gap-3 rounded-md border p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="m-0 text-lg font-semibold">Keep the momentum</h2>
            <p className="text-muted-foreground text-sm">Next up: {nextCourse.course?.name}</p>
          </div>
          <Button asChild size="lg">
            <Link to={`/accounts/${entityId}/students/courses/${nextCourse.id}`}>
              Start next module
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </section>
      )}

      <article aria-label="Course Results" className="flex flex-col gap-6">
        <div className="flex flex-col gap-0.5">
          <h2 className="text-lg font-semibold">Module Results</h2>
          {stats.steps > 0 && (
            <p className="text-muted-foreground text-sm">
              {stats.steps} {stats.steps === 1 ? 'step' : 'steps'} complete
              {stats.hasQuizzes && (
                <>
                  {' · '}
                  {stats.quizzesPassed} {stats.quizzesPassed === 1 ? 'quiz' : 'quizzes'} passed
                </>
              )}
            </p>
          )}
        </div>
        <ol className="border-border bg-card divide-border flex flex-col divide-y rounded-md border">
          {orderedContentRecords.map((contentRecord, index) => (
            <ContentRecord
              contentRecord={contentRecord}
              key={contentRecord.id}
              openToContent={openToContent}
              entityId={entityId}
              includeReviewLink={includeReviewLink}
              isInstructorView={isInstructorView}
              index={index}
            />
          ))}
        </ol>
      </article>
    </div>
  )
}

export default CourseComplete

CourseComplete.propTypes = {
  courseRecord: PropTypes.object.isRequired,
  openToContent: PropTypes.func.isRequired,
  entityId: PropTypes.string.isRequired,
  includeReviewLink: PropTypes.bool,
  isInstructorView: PropTypes.bool,
}

function ContentRecord({
  contentRecord,
  openToContent,
  entityId,
  includeReviewLink,
  isInstructorView,
  index,
}) {
  const hasQuiz = !!contentRecord.content?.quiz
  const isQuizOnly = contentRecord.content.type === 'Quiz Only'
  const { type } = contentRecord.content
  const Icon = TYPE_ICON[type] ?? FileText
  const name = isQuizOnly
    ? 'Course Quiz'
    : contentRecord.content.name || contentRecord.content.type
  const status = contentRecord.status || 'Not Started'
  return (
    <li className="flex flex-col px-4 py-3" aria-labelledby={`header-${contentRecord.id}`}>
      <div className="flex items-center gap-3 text-sm">
        <StatusIcon status={status === 'Quiz Pending' ? 'In Progress' : status} size="small" />
        <span className="text-muted-foreground tabular-nums">{index + 1}.</span>
        <Icon className="text-muted-foreground h-4 w-4 shrink-0" aria-hidden="true" />
        <span
          id={`header-${contentRecord.id}`}
          className="text-foreground flex-1 truncate font-medium"
        >
          {name}
        </span>
        {!isQuizOnly && includeReviewLink && (
          <Button variant="outline" size="sm" onClick={() => openToContent(contentRecord.id)}>
            Review Content
          </Button>
        )}
      </div>
      {hasQuiz && (
        // Indent quiz detail under the row's name column so the results
        // read as belonging to the step above.
        <div className="pl-9">
          {isInstructorView ? (
            <QuizAttemptsPanel
              contentRecord={contentRecord}
              entityId={entityId}
              attempts={contentRecord.quizAttempts}
            />
          ) : (
            <QuizRecord contentRecord={contentRecord} entityId={entityId} index={index} />
          )}
        </div>
      )}
    </li>
  )
}
ContentRecord.propTypes = {
  contentRecord: PropTypes.object.isRequired,
  openToContent: PropTypes.func.isRequired,
  entityId: PropTypes.string.isRequired,
  includeReviewLink: PropTypes.bool,
  isInstructorView: PropTypes.bool,
  index: PropTypes.number.isRequired,
}

function QuizRecord({ contentRecord, entityId, index }) {
  const { data: quizResponse } = useQuery({
    queryKey: ['accounts', 'students', 'course', 'quiz', contentRecord.id],
    queryFn: () => getQuizResult(entityId, contentRecord.id),
  })

  const score = useMemo(() => {
    if (!quizResponse) return null
    return scoreFormatter(quizResponse?.score, contentRecord.content.quiz.numQuestionsToDisplay)
  }, [quizResponse, contentRecord])

  return (
    <Accordion
      type="single"
      collapsible
      className="w-full"
      defaultValue={index === 0 ? contentRecord.id : undefined}
    >
      <AccordionItem key={contentRecord.id} value={contentRecord.id}>
        <AccordionTrigger>
          <h4>Quiz Results</h4>
          <p>
            <strong>Score:</strong> {score}
          </p>
        </AccordionTrigger>
        <AccordionContent>
          {quizResponse && (
            <QuizResult quiz={contentRecord.content.quiz} gradedQuiz={quizResponse} />
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}
QuizRecord.propTypes = {
  contentRecord: PropTypes.object.isRequired,
  entityId: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
}
